import { CaretDown } from "@phosphor-icons/react";
import { forwardRef, useId, useState, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export type CollapsibleProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  title: ReactNode;
  description?: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const Collapsible = forwardRef<HTMLDivElement, CollapsibleProps>(({ className, title, description, open, defaultOpen, onOpenChange, children, ...props }, ref) => {
  const [internalOpen, setInternalOpen] = useState(Boolean(defaultOpen));
  const panelId = useId();
  const isOpen = open ?? internalOpen;
  const setOpen = (nextOpen: boolean) => {
    if (open === undefined) setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  return (
    <div ref={ref} className={cn("bfrs-rounded-xl bfrs-border bfrs-border-border bfrs-bg-surface-card", className)} {...props}>
      <button type="button" className="bfrs-flex bfrs-w-full bfrs-items-center bfrs-gap-3 bfrs-px-4 bfrs-py-3 bfrs-text-left bfrs-outline-none focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus/25" aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpen(!isOpen)}>
        <span className="bfrs-min-w-0 bfrs-flex-1">
          <span className="bfrs-block bfrs-text-sm bfrs-font-semibold bfrs-text-text-primary">{title}</span>
          {description && <span className="bfrs-mt-0.5 bfrs-block bfrs-text-xs bfrs-leading-5 bfrs-text-text-secondary">{description}</span>}
        </span>
        <CaretDown size={16} weight="bold" className={cn("bfrs-shrink-0 bfrs-text-text-muted bfrs-transition-transform", isOpen && "bfrs-rotate-180")} />
      </button>
      {isOpen && (
        <div id={panelId} className="bfrs-border-t bfrs-border-border bfrs-px-4 bfrs-py-3">
          {children}
        </div>
      )}
    </div>
  );
});

Collapsible.displayName = "Collapsible";
